<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import TimelineAPI from 'dashboard/api/timeline';
import { usePipelinesStore } from 'dashboard/stores/pipelines';
import { resolveTimelineFieldNames } from 'dashboard/helper/timelineFieldResolvers';
import SpinnerLoader from 'dashboard/components-next/spinner/Spinner.vue';
import TimelineItem from './TimelineItem.vue';

const props = defineProps({
  subjectType: { type: String, required: true },
  subjectId: { type: [Number, String], required: true },
});

const { t } = useI18n();
const pipelinesStore = usePipelinesStore();
const events = ref([]);
const isLoading = ref(true);
const fieldNames = ref({ company_id: new Map(), contact_id: new Map() });

const resolveCompanyName = id => fieldNames.value.company_id.get(id) ?? id;
const resolveContactName = id => fieldNames.value.contact_id.get(id) ?? id;

const fetchTimeline = async () => {
  isLoading.value = true;
  try {
    const { data } = await TimelineAPI.get(props.subjectType, props.subjectId);
    events.value = data;
    fieldNames.value = await resolveTimelineFieldNames(events.value);
  } finally {
    isLoading.value = false;
  }
};

watch(
  () => [props.subjectType, props.subjectId],
  () => {
    fetchTimeline();
    if (!pipelinesStore.getPipelinesList.length) pipelinesStore.get();
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex flex-col gap-2">
    <div v-if="isLoading" class="flex justify-center py-6">
      <SpinnerLoader :size="24" class="text-n-blue-9" />
    </div>
    <p
      v-else-if="!events.length"
      class="text-sm text-n-slate-11 text-center py-4"
    >
      {{ t('TIMELINE.EMPTY') }}
    </p>
    <TimelineItem
      v-for="event in events"
      :key="`${event.subject_type}-${event.id}`"
      :event="event"
      :resolve-company-name="resolveCompanyName"
      :resolve-contact-name="resolveContactName"
    />
  </div>
</template>
